<script setup>
import { computed, reactive, ref, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import {
  useStore,
  useMapGetter,
  useStoreGetters,
} from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import { loadDefaultActionButtons } from 'dashboard/helper/telegramCampaignDefaults';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign.js';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TelegramCampaignPreview from 'dashboard/components-next/Campaigns/Pages/CampaignPage/TelegramCampaign/TelegramCampaignPreview.vue';
import telegramAudienceAPI from 'dashboard/routes/dashboard/settings/telegram/api/telegramAudienceAPI';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const getters = useStoreGetters();

const labels = useMapGetter('labels/getLabels');
const inboxes = useMapGetter('inboxes/getTelegramInboxes');
const campaignUiFlags = useMapGetter('campaigns/getUIFlags');

const mode = computed(() =>
  route.name === 'campaigns_telegram_edit' ? 'edit' : 'create'
);
const campaignId = computed(() => Number(route.params.id || 0));

const allCampaigns = computed(() => getters['campaigns/getAllCampaigns'].value);
const selectedCampaign = computed(() =>
  allCampaigns.value.find(item => Number(item.id) === campaignId.value)
);
const templateCampaignId = computed(() =>
  Number(route.query.template_from || 0)
);
const templateCampaign = computed(() =>
  allCampaigns.value.find(item => Number(item.id) === templateCampaignId.value)
);

const state = reactive({
  title: '',
  inboxId: null,
  segmentRecipients: false,
  selectedAudience: [],
  sendNow: false,
  scheduledAt: null,
  buttons: [],
  primaryBlock: {
    id: 'primary',
    type: 'text',
    delayMs: 0,
    messageEffectId: '',
    text: '',
    caption: '',
    mediaUrl: '',
    buttons: [],
    items: [],
    cards: [],
    starCount: 1,
    payload: '',
    followUpText: '',
    companionText: '',
    performer: '',
    title: '',
    duration: '',
    length: '',
  },
  blocks: [],
  newBlockType: 'text',
});

const formErrors = reactive({
  title: '',
  inbox: '',
  scheduledAt: '',
  primary: '',
  audience: '',
  buttons: '',
  blocks: '',
});

const mediaUploadState = reactive({});
const mediaUploadedNames = reactive({});
const defaultActionButtons = ref([]);

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const botOptions = computed(() => mapToOptions(inboxes.value, 'id', 'name'));
const audienceOptions = computed(() =>
  mapToOptions(labels.value, 'id', 'title')
);

const messageEffectOptions = [
  { label: '-', value: '' },
  { label: '🔥', value: '5104841245755180586' },
  { label: '👍', value: '5107584321108051014' },
  { label: '👎', value: '5104858069142078462' },
  { label: '❤️', value: '5159385139981059251' },
  { label: '🎉', value: '5046509860389126442' },
  { label: '💩', value: '5046589136895476101' },
];

const selectedBot = computed(() => {
  const targetId = state.inboxId?.toString();
  if (!targetId) return null;

  return inboxes.value.find(item => item?.id?.toString() === targetId) || null;
});

const resolvedBotAvatar = ref('');

const previewAvatarUrl = computed(
  () =>
    resolvedBotAvatar.value ||
    selectedBot.value?.avatar_url ||
    selectedBot.value?.thumbnail ||
    selectedBot.value?.channel?.avatar_url ||
    selectedBot.value?.channel?.photo_url ||
    ''
);

watch(
  () => selectedBot.value?.id,
  async botId => {
    resolvedBotAvatar.value = '';
    if (!botId) return;

    try {
      const response = await telegramAudienceAPI.getBotSettings({
        botInboxId: botId,
      });
      const url = String(response.data?.payload?.bot_avatar_url || '').trim();
      if (url) resolvedBotAvatar.value = url;
    } catch {
      // avatar fetch is best-effort
    }
  },
  { immediate: true }
);

const previewButtons = computed(() =>
  state.buttons
    .map(button => ({
      text: button.text?.trim(),
      url: button.url?.trim(),
      open_in_app_popup: Boolean(button.openInAppPopup),
    }))
    .filter(button => button.text && button.url)
);

const buildPayloadBlock = (block, overrideButtons = null) => {
  const buttonsPayload =
    overrideButtons ||
    block.buttons
      .map(button => ({
        text: button.text?.trim(),
        url: button.url?.trim(),
        open_in_app_popup: Boolean(button.openInAppPopup),
      }))
      .filter(button => button.text && button.url);

  const base = {
    id: block.id,
    type: block.type,
    message_effect_id: block.messageEffectId || null,
    text: block.text?.trim(),
    caption: block.caption?.trim(),
    media_url: block.mediaUrl?.trim(),
    delay_ms: Number(block.delayMs) || 0,
    buttons: buttonsPayload,
  };

  if (block.type === 'audio') {
    base.performer = block.performer?.trim();
    base.title = block.title?.trim();
    base.duration = block.duration ? Number(block.duration) : null;
  }

  if (block.type === 'video_note') {
    base.companion_text = block.companionText?.trim();
    base.duration = block.duration ? Number(block.duration) : null;
    base.length = block.length ? Number(block.length) : null;
  }

  if (block.type === 'media_group') {
    base.items = block.items
      .map(item => ({
        type: item.type,
        media_url: item.mediaUrl?.trim(),
        caption: item.caption?.trim(),
      }))
      .filter(item => item.type && item.media_url);
    base.follow_up_text = block.followUpText?.trim();
  }

  if (block.type === 'carousel') {
    base.cards = block.cards
      .map(card => ({
        media_type: card.mediaType,
        media_url: card.mediaUrl?.trim(),
        caption: card.caption?.trim(),
        buttons: card.buttons
          .map(button => ({
            text: button.text?.trim(),
            url: button.url?.trim(),
            open_in_app_popup: Boolean(button.openInAppPopup),
          }))
          .filter(button => button.text && button.url),
      }))
      .filter(card => card.media_url || card.caption);
  }

  if (block.type === 'paid_media') {
    base.star_count = Number(block.starCount) || 0;
    base.payload = block.payload?.trim();
    base.caption = block.caption?.trim();
    base.items = block.items
      .map(item => ({
        type: item.type,
        media_url: item.mediaUrl?.trim(),
      }))
      .filter(item => item.type && item.media_url);
  }

  return base;
};

const previewPrimaryBlock = computed(() =>
  buildPayloadBlock(state.primaryBlock, previewButtons.value)
);

const previewBlocks = computed(() => [
  previewPrimaryBlock.value,
  ...state.blocks.map(block => buildPayloadBlock(block)),
]);

const isSubmitting = computed(() =>
  mode.value === 'edit'
    ? campaignUiFlags.value.isUpdating
    : campaignUiFlags.value.isCreating
);

const toDateTimeLocalInputValue = date => {
  const localTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
};

const currentDateTime = ref(toDateTimeLocalInputValue(new Date()));
let scheduleMinTimer = null;

const BLOCK_TYPE_OPTIONS = computed(() => [
  {
    value: 'text',
    label: t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.BLOCK_TYPES.TEXT'),
  },
  {
    value: 'image',
    label: t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.BLOCK_TYPES.IMAGE'),
  },
  {
    value: 'video',
    label: t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.BLOCK_TYPES.VIDEO'),
  },
  {
    value: 'file',
    label: t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.BLOCK_TYPES.FILE'),
  },
  {
    value: 'audio',
    label: t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.BLOCK_TYPES.AUDIO'),
  },
  {
    value: 'video_note',
    label: t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.BLOCK_TYPES.VIDEO_NOTE'),
  },
  {
    value: 'media_group',
    label: t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.BLOCK_TYPES.MEDIA_GROUP'),
  },
  {
    value: 'carousel',
    label: t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.BLOCK_TYPES.CAROUSEL'),
  },
  {
    value: 'paid_media',
    label: t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.BLOCK_TYPES.PAID_MEDIA'),
  },
]);

const pageTitle = computed(() =>
  mode.value === 'edit'
    ? t('CAMPAIGN.TELEGRAM.EDITOR.EDIT_TITLE')
    : t('CAMPAIGN.TELEGRAM.EDITOR.CREATE_TITLE')
);

const submitLabel = computed(() =>
  mode.value === 'edit'
    ? t('CAMPAIGN.TELEGRAM.EDITOR.SAVE_BUTTON')
    : t('CAMPAIGN.TELEGRAM.EDITOR.CREATE_BUTTON')
);

const warningText = computed(() => t('CAMPAIGN.TELEGRAM.EDITOR.WARNING'));

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const formatEpochToDateTimeLocal = epochSeconds => {
  if (!epochSeconds) return null;
  const date = new Date(epochSeconds * 1000);
  return toDateTimeLocalInputValue(date);
};

const refreshCurrentDateTime = () => {
  currentDateTime.value = toDateTimeLocalInputValue(new Date());
};

const currentMinuteTimestamp = () => {
  const now = new Date();
  now.setSeconds(0, 0);
  return now.getTime();
};

const isPastScheduledAt = localDateTime => {
  if (!localDateTime) return false;
  const scheduledTime = new Date(localDateTime).getTime();
  if (!Number.isFinite(scheduledTime)) return false;
  return scheduledTime < currentMinuteTimestamp();
};

const resetErrors = () => {
  Object.keys(formErrors).forEach(key => {
    formErrors[key] = '';
  });
};

const addButton = () => {
  if (state.buttons.length >= 8) return;
  state.buttons.push({ text: '', url: '', openInAppPopup: false });
};

const removeButton = index => {
  state.buttons.splice(index, 1);
};

const refreshDefaultActionButtons = () => {
  defaultActionButtons.value = loadDefaultActionButtons(route.params.accountId);
};

const hasDefaultActionButtons = computed(
  () => defaultActionButtons.value.length > 0
);

const applyDefaultButtonsToPrimary = () => {
  const normalized = loadDefaultActionButtons(route.params.accountId).map(
    button => ({
      text: button.text || '',
      url: button.url || '',
      openInAppPopup: Boolean(button.openInAppPopup),
    })
  );

  state.buttons = normalized;
  if (!state.buttons.length) addButton();
  formErrors.buttons = '';
};

const clearReactiveObject = target => {
  Object.keys(target).forEach(key => {
    delete target[key];
  });
};

const createBlockId = () =>
  `block-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const normalizeBlockType = type => {
  const normalized = (type || '')
    .toString()
    .trim()
    .toLowerCase()
    .replaceAll(' ', '_');
  if (normalized === 'photo') return 'image';
  if (normalized === 'document') return 'file';
  if (normalized === 'videonote') return 'video_note';
  return normalized || 'text';
};

const createBlock = type => {
  const blockType = normalizeBlockType(type);
  const baseBlock = {
    id: createBlockId(),
    type: blockType,
    delayMs: 0,
    messageEffectId: '',
    text: '',
    caption: '',
    mediaUrl: '',
    buttons: [],
    items: [],
    cards: [],
    starCount: 1,
    payload: '',
    followUpText: '',
    companionText: '',
    performer: '',
    title: '',
    duration: '',
    length: '',
  };

  if (blockType === 'media_group') {
    baseBlock.items = [
      { type: 'photo', mediaUrl: '', caption: '' },
      { type: 'photo', mediaUrl: '', caption: '' },
    ];
  }

  if (blockType === 'carousel') {
    baseBlock.cards = [
      {
        mediaType: 'image',
        mediaUrl: '',
        caption: '',
        buttons: [],
      },
    ];
  }

  if (blockType === 'paid_media') {
    baseBlock.items = [{ type: 'image', mediaUrl: '' }];
  }

  return baseBlock;
};

const PRIMARY_BLOCK_TYPES = [
  'text',
  'image',
  'video',
  'file',
  'audio',
  'media_group',
];

const PRIMARY_BLOCK_TYPE_OPTIONS = computed(() =>
  BLOCK_TYPE_OPTIONS.value.filter(option =>
    PRIMARY_BLOCK_TYPES.includes(option.value)
  )
);

const primaryBlockType = computed({
  get: () => state.primaryBlock.type,
  set: nextType => {
    const nextBlock = createBlock(nextType);
    nextBlock.id = state.primaryBlock.id || 'primary';
    state.primaryBlock = { ...nextBlock, buttons: [] };
    formErrors.primary = '';
  },
});

const addBlock = () => {
  state.blocks.push(createBlock(state.newBlockType));
};

const removeBlock = index => {
  state.blocks.splice(index, 1);
};

const addBlockButton = block => {
  if (block.buttons.length >= 8) return;
  block.buttons.push({ text: '', url: '', openInAppPopup: false });
};

const removeBlockButton = (block, index) => {
  block.buttons.splice(index, 1);
};

const addMediaGroupItem = block => {
  if (block.items.length >= 10) return;
  block.items.push({ type: 'photo', mediaUrl: '', caption: '' });
};

const removeMediaGroupItem = (block, index) => {
  block.items.splice(index, 1);
};

const addCarouselCard = block => {
  block.cards.push({
    mediaType: 'image',
    mediaUrl: '',
    caption: '',
    buttons: [],
  });
};

const removeCarouselCard = (block, index) => {
  block.cards.splice(index, 1);
};

const addCarouselCardButton = card => {
  if (card.buttons.length >= 8) return;
  card.buttons.push({ text: '', url: '', openInAppPopup: false });
};

const removeCarouselCardButton = (card, index) => {
  card.buttons.splice(index, 1);
};

const addPaidMediaItem = block => {
  if (block.items.length >= 10) return;
  block.items.push({ type: 'image', mediaUrl: '' });
};

const removePaidMediaItem = (block, index) => {
  block.items.splice(index, 1);
};

const buildMediaUploadKey = (...parts) => parts.join(':');

const blockMediaUploadKey = block =>
  buildMediaUploadKey('block', block.id, 'media');

const mediaGroupItemUploadKey = (block, itemIndex) =>
  buildMediaUploadKey('block', block.id, 'media-group-item', itemIndex);

const carouselCardUploadKey = (block, cardIndex) =>
  buildMediaUploadKey('block', block.id, 'carousel-card', cardIndex);

const paidMediaItemUploadKey = (block, itemIndex) =>
  buildMediaUploadKey('block', block.id, 'paid-item', itemIndex);

const isMediaUploading = key => Boolean(mediaUploadState[key]);

const isImageOrVideoBlock = block => ['image', 'video'].includes(block.type);

const isUploadableMediaGroupItem = item =>
  ['photo', 'video'].includes((item?.type || '').toString().toLowerCase());

const mediaGroupAccept = item =>
  (item?.type || '').toString().toLowerCase() === 'video'
    ? 'video/*'
    : 'image/*';

const getFilenameFromUrl = url => {
  if (!url) return '';
  try {
    const normalized = decodeURIComponent(url);
    const name = normalized.split('/').pop()?.split('?')[0];
    return name || '';
  } catch (_error) {
    return '';
  }
};

const mediaUploadLabel = (key, fallbackUrl = '') =>
  mediaUploadedNames[key] ||
  getFilenameFromUrl(fallbackUrl) ||
  t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.UPLOAD_PLACEHOLDER');

const uploadBlockMediaFile = async ({ file, key, onSuccess }) => {
  mediaUploadState[key] = true;
  try {
    const uploaded = await uploadFile(file);
    onSuccess(uploaded.fileUrl);
    mediaUploadedNames[key] = file.name;
  } catch (error) {
    useAlert(t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.UPLOAD_ERROR'));
  } finally {
    mediaUploadState[key] = false;
  }
};

const handleBlockMediaUpload = async (event, block) => {
  const [file] = event.target.files || [];
  if (!file) return;

  const key = blockMediaUploadKey(block);
  await uploadBlockMediaFile({
    file,
    key,
    onSuccess: fileUrl => {
      block.mediaUrl = fileUrl;
    },
  });
  event.target.value = '';
};

const handleMediaGroupItemUpload = async (event, block, item, itemIndex) => {
  const [file] = event.target.files || [];
  if (!file) return;

  const key = mediaGroupItemUploadKey(block, itemIndex);
  await uploadBlockMediaFile({
    file,
    key,
    onSuccess: fileUrl => {
      item.mediaUrl = fileUrl;
    },
  });
  event.target.value = '';
};

const handleCarouselCardUpload = async (event, block, card, cardIndex) => {
  const [file] = event.target.files || [];
  if (!file) return;

  const key = carouselCardUploadKey(block, cardIndex);
  await uploadBlockMediaFile({
    file,
    key,
    onSuccess: fileUrl => {
      card.mediaUrl = fileUrl;
    },
  });
  event.target.value = '';
};

const handlePaidMediaItemUpload = async (event, block, item, itemIndex) => {
  const [file] = event.target.files || [];
  if (!file) return;

  const key = paidMediaItemUploadKey(block, itemIndex);
  await uploadBlockMediaFile({
    file,
    key,
    onSuccess: fileUrl => {
      item.mediaUrl = fileUrl;
    },
  });
  event.target.value = '';
};

const validateButtons = () => {
  const isValidHttpUrl = url => /^https?:\/\//i.test(url);

  const hasInvalidButton = state.buttons.some(button => {
    const text = button.text?.trim();
    const url = button.url?.trim();
    const partiallyFilled = Boolean(text) !== Boolean(url);
    const invalidUrl = Boolean(url) && !isValidHttpUrl(url);
    return partiallyFilled || invalidUrl;
  });

  if (hasInvalidButton) {
    formErrors.buttons = t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BUTTONS.ERROR');
    return false;
  }

  return true;
};

const validateBlocks = () => {
  const isValidHttpUrl = url => /^https?:\/\//i.test(url);

  const hasInvalidBlock = state.blocks.some(block => {
    const hasInvalidButtons = block.buttons.some(button => {
      const text = button.text?.trim();
      const url = button.url?.trim();
      return Boolean(text) !== Boolean(url) || (url && !isValidHttpUrl(url));
    });
    if (hasInvalidButtons) return true;

    if (block.type === 'text') return !block.text?.trim();

    if (
      ['image', 'video', 'file', 'audio', 'video_note'].includes(block.type)
    ) {
      return !block.mediaUrl?.trim();
    }

    if (block.type === 'media_group') {
      if (block.items.length < 2 || block.items.length > 10) return true;
      return block.items.some(item => !item.mediaUrl?.trim() || !item.type);
    }

    if (block.type === 'carousel') {
      if (!block.cards.length) return true;
      const hasInvalidCards = block.cards.some(card => {
        const hasCardInvalidButtons = card.buttons.some(button => {
          const text = button.text?.trim();
          const url = button.url?.trim();
          return (
            Boolean(text) !== Boolean(url) || (url && !isValidHttpUrl(url))
          );
        });
        return (
          hasCardInvalidButtons || !card.mediaUrl?.trim() || !card.mediaType
        );
      });
      return hasInvalidCards;
    }

    if (block.type === 'paid_media') {
      if (Number(block.starCount) < 1 || Number(block.starCount) > 25000)
        return true;
      if (!block.items.length || block.items.length > 10) return true;
      return block.items.some(item => !item.type || !item.mediaUrl?.trim());
    }

    return false;
  });

  if (hasInvalidBlock) {
    formErrors.blocks =
      'Some additional posts are incomplete. Please fill required fields and upload/select media.';
    return false;
  }

  return true;
};

const validatePrimaryBlock = () => {
  const block = state.primaryBlock;

  const isBlank = value => !value?.toString().trim();

  if (block.type === 'text') {
    if (isBlank(block.text)) {
      formErrors.primary = t('CAMPAIGN.TELEGRAM.EDITOR.FORM.DETAILS.ERROR');
      return false;
    }
    return true;
  }

  if (['image', 'video', 'file', 'audio', 'video_note'].includes(block.type)) {
    if (isBlank(block.mediaUrl)) {
      formErrors.primary = t('CAMPAIGN.TELEGRAM.EDITOR.FORM.DETAILS.ERROR');
      return false;
    }
    return true;
  }

  if (block.type === 'media_group') {
    const items = Array.isArray(block.items) ? block.items : [];
    if (items.length < 2 || items.length > 10) {
      formErrors.primary = t('CAMPAIGN.TELEGRAM.EDITOR.FORM.DETAILS.ERROR');
      return false;
    }

    const hasInvalidItems = items.some(
      item => isBlank(item?.mediaUrl) || isBlank(item?.type)
    );
    if (hasInvalidItems) {
      formErrors.primary = t('CAMPAIGN.TELEGRAM.EDITOR.FORM.DETAILS.ERROR');
      return false;
    }

    return true;
  }

  formErrors.primary = t('CAMPAIGN.TELEGRAM.EDITOR.FORM.DETAILS.ERROR');
  return false;
};

const validateForm = () => {
  resetErrors();

  if (!state.title.trim()) {
    formErrors.title = t('CAMPAIGN.TELEGRAM.EDITOR.FORM.TITLE.ERROR');
  }

  if (!state.inboxId) {
    formErrors.inbox = t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BOT.ERROR');
  }

  if (!state.sendNow && !state.scheduledAt) {
    formErrors.scheduledAt = t(
      'CAMPAIGN.TELEGRAM.EDITOR.FORM.SCHEDULED_AT.ERROR'
    );
  } else if (!state.sendNow && isPastScheduledAt(state.scheduledAt)) {
    formErrors.scheduledAt = t(
      'CAMPAIGN.TELEGRAM.EDITOR.FORM.SCHEDULED_AT.PAST_ERROR'
    );
  }

  if (state.segmentRecipients && state.selectedAudience.length === 0) {
    formErrors.audience = t('CAMPAIGN.TELEGRAM.EDITOR.FORM.SEGMENT.ERROR');
  }

  validatePrimaryBlock();
  validateButtons();
  validateBlocks();

  return Object.values(formErrors).every(message => !message);
};

const resetEditorState = () => {
  state.title = '';
  state.inboxId = null;
  state.segmentRecipients = false;
  state.selectedAudience = [];
  state.sendNow = false;
  state.scheduledAt = currentDateTime.value;
  state.buttons = [];
  state.primaryBlock = { ...createBlock('text'), id: 'primary', buttons: [] };
  state.blocks = [];
  state.newBlockType = 'text';
  resetErrors();
  clearReactiveObject(mediaUploadState);
  clearReactiveObject(mediaUploadedNames);
};

const buildPayload = () => {
  const resolvedSchedule = state.sendNow
    ? formatToUTCString(currentDateTime.value)
    : formatToUTCString(state.scheduledAt);

  const primaryMessageFallback =
    state.primaryBlock.type === 'text'
      ? state.primaryBlock.text?.trim()
      : state.primaryBlock.caption?.trim() ||
        state.primaryBlock.text?.trim() ||
        (state.primaryBlock.type === 'media_group'
          ? state.primaryBlock.followUpText?.trim()
          : '');

  return {
    title: state.title.trim(),
    message: primaryMessageFallback || '',
    inbox_id: state.inboxId,
    scheduled_at: resolvedSchedule,
    audience: state.segmentRecipients
      ? state.selectedAudience.map(id => ({ id, type: 'Label' }))
      : [],
    template_params: {
      telegram: {
        blocks_include_primary: true,
        image_url: null,
        buttons: [],
        blocks: previewBlocks.value,
      },
    },
  };
};

const backToList = () => {
  router.push({
    name: 'campaigns_telegram_index',
    params: { accountId: route.params.accountId },
  });
};

const submitCampaign = async () => {
  if (!validateForm()) return;

  const payload = buildPayload();

  try {
    if (mode.value === 'edit') {
      await store.dispatch('campaigns/update', {
        id: campaignId.value,
        ...payload,
      });
      useAlert(t('CAMPAIGN.TELEGRAM.EDIT.FORM.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('campaigns/create', payload);
      useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
        type: CAMPAIGN_TYPES.ONE_OFF,
      });
      useAlert(t('CAMPAIGN.TELEGRAM.CREATE.FORM.API.SUCCESS_MESSAGE'));
    }

    backToList();
  } catch (error) {
    const fallbackMessage =
      mode.value === 'edit'
        ? t('CAMPAIGN.TELEGRAM.EDIT.FORM.API.ERROR_MESSAGE')
        : t('CAMPAIGN.TELEGRAM.CREATE.FORM.API.ERROR_MESSAGE');
    useAlert(error?.response?.message || fallbackMessage);
  }
};

const applyCampaignToState = (campaign, options = {}) => {
  if (!campaign) return;
  const asTemplate = Boolean(options.asTemplate);

  const audience = Array.isArray(campaign.audience) ? campaign.audience : [];
  const selectedAudience = audience
    .filter(item => item.type === 'Label')
    .map(item => item.id);

  const telegramParams = campaign.template_params?.telegram || {};
  const legacyMessageEffectId = telegramParams.message_effect_id || '';
  const blocksIncludePrimary = Boolean(
    telegramParams.blocks_include_primary || telegramParams.blocksIncludePrimary
  );
  const legacyButtons = Array.isArray(telegramParams.buttons)
    ? telegramParams.buttons.map(button => ({
        text: button.text || '',
        url: button.url || button.web_app?.url || '',
        openInAppPopup: Boolean(
          button.open_in_app_popup || button.openInAppPopup || button.web_app
        ),
      }))
    : [];

  const blocks = Array.isArray(telegramParams.blocks)
    ? telegramParams.blocks.map(block => {
        const type = normalizeBlockType(block.type);
        const mappedBlock = createBlock(type);
        mappedBlock.id = block.id || createBlockId();
        mappedBlock.delayMs = Number(block.delay_ms || 0);
        mappedBlock.messageEffectId =
          block.message_effect_id || block.messageEffectId || '';
        mappedBlock.text = block.text || '';
        mappedBlock.caption = block.caption || '';
        mappedBlock.mediaUrl = block.media_url || block.image_url || '';
        mappedBlock.buttons = Array.isArray(block.buttons)
          ? block.buttons.map(button => ({
              text: button.text || '',
              url: button.url || button.web_app?.url || '',
              openInAppPopup: Boolean(
                button.open_in_app_popup ||
                  button.openInAppPopup ||
                  button.web_app
              ),
            }))
          : [];

        if (type === 'audio') {
          mappedBlock.performer = block.performer || '';
          mappedBlock.title = block.title || '';
          mappedBlock.duration = block.duration || '';
        }

        if (type === 'video_note') {
          mappedBlock.companionText = block.companion_text || '';
          mappedBlock.duration = block.duration || '';
          mappedBlock.length = block.length || '';
        }

        if (type === 'media_group') {
          mappedBlock.items = Array.isArray(block.items)
            ? block.items.map(item => ({
                type: item.type || 'photo',
                mediaUrl: item.media_url || item.media || '',
                caption: item.caption || '',
              }))
            : createBlock('media_group').items;
          mappedBlock.followUpText =
            block.follow_up_text || block.buttons_message || '';
        }

        if (type === 'carousel') {
          mappedBlock.cards = Array.isArray(block.cards)
            ? block.cards.map(card => ({
                mediaType: card.media_type || card.mediaType || 'image',
                mediaUrl: card.media_url || card.media || '',
                caption: card.caption || card.text || '',
                buttons: Array.isArray(card.buttons)
                  ? card.buttons.map(button => ({
                      text: button.text || '',
                      url: button.url || button.web_app?.url || '',
                      openInAppPopup: Boolean(
                        button.open_in_app_popup ||
                          button.openInAppPopup ||
                          button.web_app
                      ),
                    }))
                  : [],
              }))
            : createBlock('carousel').cards;
        }

        if (type === 'paid_media') {
          mappedBlock.starCount = Number(block.star_count || 1);
          mappedBlock.payload = block.payload || '';
          mappedBlock.caption = block.caption || '';
          mappedBlock.items = Array.isArray(block.items)
            ? block.items.map(item => ({
                type: item.type || 'image',
                mediaUrl: item.media_url || item.media || '',
              }))
            : createBlock('paid_media').items;
        }

        return mappedBlock;
      })
    : [];

  const legacyPrimaryType = telegramParams.image_url ? 'image' : 'text';
  const legacyPrimaryBlock = createBlock(legacyPrimaryType);
  legacyPrimaryBlock.id = 'primary';
  legacyPrimaryBlock.messageEffectId = legacyMessageEffectId;
  legacyPrimaryBlock.mediaUrl = telegramParams.image_url || '';
  if (legacyPrimaryType === 'image') {
    legacyPrimaryBlock.caption = campaign.message || '';
  } else {
    legacyPrimaryBlock.text = campaign.message || '';
  }

  state.title = campaign.title || '';
  state.inboxId = campaign.inbox?.id || null;
  state.segmentRecipients = selectedAudience.length > 0;
  state.selectedAudience = selectedAudience;
  state.sendNow = asTemplate ? false : !campaign.scheduled_at;
  state.scheduledAt = asTemplate
    ? currentDateTime.value
    : formatEpochToDateTimeLocal(campaign.scheduled_at);
  if (blocksIncludePrimary && blocks.length) {
    const [primary, ...additional] = blocks;
    if (!primary.messageEffectId && legacyMessageEffectId) {
      primary.messageEffectId = legacyMessageEffectId;
    }
    state.primaryBlock = {
      ...createBlock(primary.type),
      ...primary,
      buttons: [],
    };
    state.buttons = Array.isArray(primary.buttons) ? primary.buttons : [];
    state.blocks = additional;
  } else {
    state.primaryBlock = legacyPrimaryBlock;
    state.buttons = legacyButtons;
    state.blocks = blocks;
  }
};

let editorLoadSeq = 0;
const initializeEditorFromRoute = async () => {
  const seq = ++editorLoadSeq;

  refreshCurrentDateTime();
  refreshDefaultActionButtons();
  resetEditorState();

  if (!labels.value?.length) {
    await store.dispatch('labels/get');
    if (seq !== editorLoadSeq) return;
  }

  if (!inboxes.value?.length) {
    await store.dispatch('inboxes/get');
    if (seq !== editorLoadSeq) return;
  }

  if (mode.value === 'edit') {
    if (!selectedCampaign.value) {
      await store.dispatch('campaigns/get');
      if (seq !== editorLoadSeq) return;
    }

    if (!selectedCampaign.value) {
      useAlert(t('CAMPAIGN.TELEGRAM.EDITOR.NOT_FOUND'));
      backToList();
      return;
    }

    applyCampaignToState(selectedCampaign.value);
    return;
  }

  if (templateCampaignId.value > 0) {
    if (!templateCampaign.value) {
      await store.dispatch('campaigns/get');
      if (seq !== editorLoadSeq) return;
    }

    if (templateCampaign.value) {
      applyCampaignToState(templateCampaign.value, { asTemplate: true });
      return;
    }
  }

  state.scheduledAt = currentDateTime.value;
  if (!state.buttons.length) addButton();
};

onMounted(async () => {
  scheduleMinTimer = window.setInterval(refreshCurrentDateTime, 30000);
  await initializeEditorFromRoute();
});

watch([mode, campaignId, templateCampaignId], () => {
  void initializeEditorFromRoute();
});

onUnmounted(() => {
  if (scheduleMinTimer) {
    window.clearInterval(scheduleMinTimer);
    scheduleMinTimer = null;
  }
});
</script>

<template>
  <div class="settings-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header class="settings-header flex-shrink-0">
      <div class="header-bg">
        <div class="header-glow" />
        <div class="header-pattern" />
      </div>
      <div class="header-inner">
        <div class="header-left">
          <button
            class="back-btn"
            :title="t('CAMPAIGN.TELEGRAM.EDITOR.BACK')"
            @click="backToList"
          >
            <span class="i-ph-arrow-left-bold size-4" />
          </button>
          <div class="header-icon-box">
            <span class="i-ph-paper-plane-right-bold size-5" />
          </div>
          <div class="header-text">
            <h1 class="header-title">{{ pageTitle }}</h1>
            <p class="header-desc">Telegram campaign configuration</p>
          </div>
        </div>
      </div>
    </header>

    <!-- Content -->
    <div class="settings-content flex-1 overflow-y-auto">
      <div class="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-0">
        <!-- Left: Form Sections -->
        <div class="settings-sections">
          <!-- Bot Selection Section -->
          <section class="settings-section">
            <div class="settings-section__info">
              <h3 class="text-base font-semibold text-n-slate-12 mb-1">
                {{ t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BOT.LABEL') }}
              </h3>
              <p class="text-sm text-n-slate-11 leading-relaxed">
                {{ t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BOT.DESCRIPTION') }}
              </p>
            </div>
            <div class="settings-section__content">
              <ComboBox
                v-model="state.inboxId"
                :options="botOptions"
                :placeholder="
                  t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BOT.PLACEHOLDER')
                "
                :has-error="!!formErrors.inbox"
                :message="formErrors.inbox"
                class="campaign-combobox"
              />

              <!-- Warning -->
              <div
                class="mt-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200"
              >
                <p class="text-sm leading-relaxed text-amber-800 m-0">
                  {{ warningText }}
                </p>
              </div>
            </div>
          </section>

          <!-- Audience Section -->
          <section class="settings-section">
            <div class="settings-section__info">
              <h3 class="text-base font-semibold text-n-slate-12 mb-1">
                {{ t('CAMPAIGN.TELEGRAM.EDITOR.FORM.SEGMENT.TITLE') }}
              </h3>
              <p class="text-sm text-n-slate-11 leading-relaxed">
                {{ t('CAMPAIGN.TELEGRAM.EDITOR.FORM.SEGMENT.DESCRIPTION') }}
              </p>
            </div>
            <div class="settings-section__content">
              <label
                class="inline-flex items-center gap-3 cursor-pointer select-none mb-4"
              >
                <input
                  v-model="state.segmentRecipients"
                  type="checkbox"
                  class="w-4 h-4 rounded border-n-slate-7 text-n-blue-9 focus:ring-n-blue-9"
                />
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('CAMPAIGN.TELEGRAM.EDITOR.FORM.SEGMENT.LABEL') }}
                </span>
              </label>

              <TagMultiSelectComboBox
                v-if="state.segmentRecipients"
                v-model="state.selectedAudience"
                :options="audienceOptions"
                :label="t('CAMPAIGN.TELEGRAM.EDITOR.FORM.SEGMENT.LABEL')"
                :placeholder="
                  t('CAMPAIGN.TELEGRAM.EDITOR.FORM.SEGMENT.PLACEHOLDER')
                "
                :has-error="!!formErrors.audience"
                :message="formErrors.audience"
                class="campaign-combobox campaign-combobox--multi"
              />
            </div>
          </section>

          <!-- Campaign Details Section -->
          <section class="settings-section">
            <div class="settings-section__info">
              <h3 class="text-base font-semibold text-n-slate-12 mb-1">
                {{ t('CAMPAIGN.TELEGRAM.EDITOR.FORM.DETAILS.TITLE') }}
              </h3>
              <p class="text-sm text-n-slate-11 leading-relaxed">
                {{ t('CAMPAIGN.TELEGRAM.EDITOR.FORM.DETAILS.DESCRIPTION') }}
              </p>
            </div>
            <div class="settings-section__content flex flex-col gap-5">
              <!-- Title -->
              <div class="settings-input-group">
                <label class="settings-input-label">
                  {{ t('CAMPAIGN.TELEGRAM.EDITOR.FORM.TITLE.LABEL') }}
                </label>
                <Input
                  v-model="state.title"
                  :placeholder="
                    t('CAMPAIGN.TELEGRAM.EDITOR.FORM.TITLE.PLACEHOLDER')
                  "
                  :message="formErrors.title"
                  :message-type="formErrors.title ? 'error' : 'info'"
                />
              </div>

              <!-- Primary post -->
              <p v-if="formErrors.primary" class="text-xs text-n-ruby-10 m-0">
                {{ formErrors.primary }}
              </p>

              <div class="settings-input-group">
                <label class="settings-input-label">
                  {{
                    t(
                      'CAMPAIGN.TELEGRAM.EDITOR.FORM.DETAILS.CONTENT_TYPE_LABEL'
                    )
                  }}
                </label>
                <ComboBox
                  v-model="primaryBlockType"
                  :options="PRIMARY_BLOCK_TYPE_OPTIONS"
                  :placeholder="
                    t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.SELECT_TYPE')
                  "
                  class="campaign-combobox"
                />
              </div>

              <template v-if="primaryBlockType === 'text'">
                <div class="settings-input-group">
                  <label class="settings-input-label">
                    {{ t('CAMPAIGN.TELEGRAM.EDITOR.FORM.MESSAGE.LABEL') }}
                  </label>
                  <TextArea
                    v-model="state.primaryBlock.text"
                    :placeholder="
                      t('CAMPAIGN.TELEGRAM.EDITOR.FORM.MESSAGE.PLACEHOLDER')
                    "
                    show-character-count
                  />
                </div>
              </template>

              <template
                v-else-if="
                  ['image', 'video', 'file', 'audio', 'video_note'].includes(
                    primaryBlockType
                  )
                "
              >
                <div class="settings-input-group">
                  <label class="settings-input-label">
                    {{
                      t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_URL_LABEL')
                    }}
                  </label>
                  <template v-if="isImageOrVideoBlock(state.primaryBlock)">
                    <div class="upload-zone upload-zone--compact">
                      <input
                        class="upload-zone__input"
                        type="file"
                        :accept="
                          state.primaryBlock.type === 'video'
                            ? 'video/*'
                            : 'image/*'
                        "
                        :disabled="
                          isMediaUploading(
                            blockMediaUploadKey(state.primaryBlock)
                          )
                        "
                        @change="
                          handleBlockMediaUpload($event, state.primaryBlock)
                        "
                      />
                      <div
                        class="upload-zone__content upload-zone__content--compact"
                      >
                        <Spinner
                          v-if="
                            isMediaUploading(
                              blockMediaUploadKey(state.primaryBlock)
                            )
                          "
                        />
                        <template v-else>
                          <span
                            class="size-5 text-n-slate-10"
                            :class="
                              state.primaryBlock.type === 'video'
                                ? 'i-ph-video-camera-bold'
                                : 'i-ph-image-bold'
                            "
                          />
                          <span class="text-sm text-n-slate-11">
                            {{
                              mediaUploadLabel(
                                blockMediaUploadKey(state.primaryBlock),
                                state.primaryBlock.mediaUrl
                              )
                            }}
                          </span>
                        </template>
                      </div>
                    </div>
                  </template>
                  <Input
                    v-else
                    v-model="state.primaryBlock.mediaUrl"
                    :placeholder="
                      t(
                        'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_URL_PLACEHOLDER'
                      )
                    "
                  />
                </div>

                <div class="settings-input-group">
                  <label class="settings-input-label">
                    {{
                      t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.CAPTION_LABEL')
                    }}
                  </label>
                  <TextArea
                    v-model="state.primaryBlock.caption"
                    :placeholder="
                      t(
                        'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.CAPTION_PLACEHOLDER'
                      )
                    "
                    show-character-count
                  />
                </div>

                <template v-if="primaryBlockType === 'audio'">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                      v-model="state.primaryBlock.performer"
                      :placeholder="
                        t(
                          'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.AUDIO_PERFORMER_PLACEHOLDER'
                        )
                      "
                    />
                    <Input
                      v-model="state.primaryBlock.title"
                      :placeholder="
                        t(
                          'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.AUDIO_TITLE_PLACEHOLDER'
                        )
                      "
                    />
                  </div>
                </template>
              </template>

              <template v-else-if="primaryBlockType === 'media_group'">
                <div class="settings-input-group">
                  <label class="settings-input-label">
                    {{
                      t(
                        'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_GROUP.ITEMS_LABEL'
                      )
                    }}
                  </label>
                  <div
                    v-for="(item, itemIndex) in state.primaryBlock.items"
                    :key="`primary-media-group-item-${itemIndex}`"
                    class="block-item-row"
                  >
                    <select v-model="item.type" class="block-select">
                      <option value="photo">
                        {{
                          t(
                            'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_GROUP.TYPE_PHOTO'
                          )
                        }}
                      </option>
                      <option value="video">
                        {{
                          t(
                            'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_GROUP.TYPE_VIDEO'
                          )
                        }}
                      </option>
                      <option value="document">
                        {{
                          t(
                            'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_GROUP.TYPE_DOCUMENT'
                          )
                        }}
                      </option>
                      <option value="audio">
                        {{
                          t(
                            'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_GROUP.TYPE_AUDIO'
                          )
                        }}
                      </option>
                    </select>
                    <Input
                      v-if="!isUploadableMediaGroupItem(item)"
                      v-model="item.mediaUrl"
                      :placeholder="
                        t(
                          'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_URL_PLACEHOLDER'
                        )
                      "
                    />
                    <div
                      v-else
                      class="upload-zone upload-zone--compact block-item-upload"
                    >
                      <input
                        class="upload-zone__input"
                        type="file"
                        :accept="mediaGroupAccept(item)"
                        :disabled="
                          isMediaUploading(
                            mediaGroupItemUploadKey(
                              state.primaryBlock,
                              itemIndex
                            )
                          )
                        "
                        @change="
                          handleMediaGroupItemUpload(
                            $event,
                            state.primaryBlock,
                            item,
                            itemIndex
                          )
                        "
                      />
                      <div
                        class="upload-zone__content upload-zone__content--compact"
                      >
                        <Spinner
                          v-if="
                            isMediaUploading(
                              mediaGroupItemUploadKey(
                                state.primaryBlock,
                                itemIndex
                              )
                            )
                          "
                        />
                        <template v-else>
                          <span
                            class="size-5 text-n-slate-10"
                            :class="
                              item.type === 'video'
                                ? 'i-ph-video-camera-bold'
                                : 'i-ph-image-bold'
                            "
                          />
                          <span class="text-xs text-n-slate-11 truncate">
                            {{
                              mediaUploadLabel(
                                mediaGroupItemUploadKey(
                                  state.primaryBlock,
                                  itemIndex
                                ),
                                item.mediaUrl
                              )
                            }}
                          </span>
                        </template>
                      </div>
                    </div>
                    <Input
                      v-model="item.caption"
                      :placeholder="
                        t(
                          'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_GROUP.CAPTION_PLACEHOLDER'
                        )
                      "
                    />
                    <Button
                      icon="i-ph-trash-bold"
                      color="ruby"
                      variant="faded"
                      size="sm"
                      @click="
                        removeMediaGroupItem(state.primaryBlock, itemIndex)
                      "
                    />
                  </div>
                  <button
                    v-if="state.primaryBlock.items.length < 10"
                    type="button"
                    class="add-button-link"
                    @click="addMediaGroupItem(state.primaryBlock)"
                  >
                    <span class="i-ph-plus-bold size-4" />
                    {{
                      t(
                        'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_GROUP.ADD_ITEM'
                      )
                    }}
                  </button>
                </div>

                <div class="settings-input-group">
                  <label class="settings-input-label">
                    {{
                      t(
                        'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_GROUP.FOLLOW_UP_TEXT_LABEL'
                      )
                    }}
                  </label>
                  <Input
                    v-model="state.primaryBlock.followUpText"
                    :placeholder="
                      t(
                        'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_GROUP.FOLLOW_UP_TEXT_PLACEHOLDER'
                      )
                    "
                  />
                </div>
              </template>

              <!-- Message appearance effect -->
              <div class="settings-input-group">
                <label class="settings-input-label">
                  {{ t('CAMPAIGN.TELEGRAM.EDITOR.FORM.MESSAGE_EFFECT.LABEL') }}
                </label>
                <ComboBox
                  v-model="state.primaryBlock.messageEffectId"
                  :options="messageEffectOptions"
                  :placeholder="
                    t(
                      'CAMPAIGN.TELEGRAM.EDITOR.FORM.MESSAGE_EFFECT.PLACEHOLDER'
                    )
                  "
                  class="campaign-combobox"
                />
              </div>
            </div>
          </section>

          <!-- Buttons Section -->
          <section class="settings-section">
            <div class="settings-section__info">
              <h3 class="text-base font-semibold text-n-slate-12 mb-1">
                {{ t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BUTTONS.TITLE') }}
              </h3>
              <p class="text-sm text-n-slate-11 leading-relaxed">
                {{ t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BUTTONS.DESCRIPTION') }}
              </p>
            </div>
            <div class="settings-section__content flex flex-col gap-3">
              <div
                v-for="(button, index) in state.buttons"
                :key="`button-${index}`"
                class="button-row"
              >
                <Input
                  v-model="button.text"
                  :placeholder="
                    t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BUTTONS.TEXT_PLACEHOLDER')
                  "
                />
                <Input
                  v-model="button.url"
                  :placeholder="
                    t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BUTTONS.URL_PLACEHOLDER')
                  "
                />
                <Button
                  icon="i-ph-trash-bold"
                  color="ruby"
                  variant="faded"
                  size="sm"
                  @click="removeButton(index)"
                />
                <label class="button-row__webapp">
                  <input
                    v-model="button.openInAppPopup"
                    type="checkbox"
                    class="w-4 h-4 rounded border-n-slate-7 text-n-blue-9 focus:ring-n-blue-9"
                  />
                  <span>{{
                    t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BUTTONS.OPEN_IN_APP_POPUP')
                  }}</span>
                </label>
              </div>
              <p v-if="formErrors.buttons" class="text-xs text-n-ruby-10 m-0">
                {{ formErrors.buttons }}
              </p>
              <button
                v-if="hasDefaultActionButtons"
                type="button"
                class="add-button-link"
                @click="applyDefaultButtonsToPrimary"
              >
                <span class="i-ph-lightning-bold size-4" />
                {{
                  t(
                    'CAMPAIGN.TELEGRAM.EDITOR.FORM.BUTTONS.APPLY_DEFAULT_ACTION_BUTTONS'
                  )
                }}
              </button>
              <button
                v-if="state.buttons.length < 8"
                type="button"
                class="add-button-link"
                @click="addButton"
              >
                <span class="i-ph-plus-bold size-4" />
                {{ t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BUTTONS.ADD') }}
              </button>
            </div>
          </section>

          <!-- Additional Posts Section -->
          <section class="settings-section">
            <div class="settings-section__info">
              <h3 class="text-base font-semibold text-n-slate-12 mb-1">
                {{ t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.TITLE') }}
              </h3>
              <p class="text-sm text-n-slate-11 leading-relaxed">
                {{ t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.DESCRIPTION') }}
              </p>
            </div>
            <div class="settings-section__content flex flex-col gap-4">
              <p v-if="formErrors.blocks" class="text-xs text-n-ruby-10 m-0">
                {{ formErrors.blocks }}
              </p>

              <div
                v-for="(block, blockIndex) in state.blocks"
                :key="block.id"
                class="campaign-block"
              >
                <div class="campaign-block__header">
                  <div class="campaign-block__title">
                    {{
                      t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.POST_TITLE', {
                        index: blockIndex + 2,
                      })
                    }}
                    {{
                      t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.POST_SEPARATOR')
                    }}
                    <span class="capitalize">{{
                      block.type.replaceAll('_', ' ')
                    }}</span>
                  </div>
                  <Button
                    icon="i-ph-trash-bold"
                    color="ruby"
                    variant="faded"
                    size="sm"
                    @click="removeBlock(blockIndex)"
                  />
                </div>

                <div class="settings-input-group">
                  <label class="settings-input-label">
                    {{ t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.DELAY_LABEL') }}
                  </label>
                  <Input
                    v-model="block.delayMs"
                    type="number"
                    min="0"
                    max="60000"
                    :placeholder="
                      t(
                        'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.DELAY_PLACEHOLDER'
                      )
                    "
                  />
                </div>

                <div class="settings-input-group">
                  <label class="settings-input-label">
                    {{
                      t('CAMPAIGN.TELEGRAM.EDITOR.FORM.MESSAGE_EFFECT.LABEL')
                    }}
                  </label>
                  <ComboBox
                    v-model="block.messageEffectId"
                    :options="messageEffectOptions"
                    :placeholder="
                      t(
                        'CAMPAIGN.TELEGRAM.EDITOR.FORM.MESSAGE_EFFECT.PLACEHOLDER'
                      )
                    "
                    class="campaign-combobox"
                  />
                </div>

                <template v-if="block.type === 'text'">
                  <div class="settings-input-group">
                    <label class="settings-input-label">
                      {{
                        t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MESSAGE_LABEL')
                      }}
                    </label>
                    <TextArea
                      v-model="block.text"
                      :placeholder="
                        t(
                          'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MESSAGE_PLACEHOLDER'
                        )
                      "
                      show-character-count
                    />
                  </div>
                </template>

                <template
                  v-if="
                    ['image', 'video', 'file', 'audio', 'video_note'].includes(
                      block.type
                    )
                  "
                >
                  <div class="settings-input-group">
                    <label class="settings-input-label">
                      {{
                        t(
                          'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_URL_LABEL'
                        )
                      }}
                    </label>
                    <template v-if="isImageOrVideoBlock(block)">
                      <div class="upload-zone upload-zone--compact">
                        <input
                          class="upload-zone__input"
                          type="file"
                          :accept="
                            block.type === 'video' ? 'video/*' : 'image/*'
                          "
                          :disabled="
                            isMediaUploading(blockMediaUploadKey(block))
                          "
                          @change="handleBlockMediaUpload($event, block)"
                        />
                        <div
                          class="upload-zone__content upload-zone__content--compact"
                        >
                          <Spinner
                            v-if="isMediaUploading(blockMediaUploadKey(block))"
                          />
                          <template v-else>
                            <span
                              class="size-5 text-n-slate-10"
                              :class="
                                block.type === 'video'
                                  ? 'i-ph-video-camera-bold'
                                  : 'i-ph-image-bold'
                              "
                            />
                            <span class="text-sm text-n-slate-11">
                              {{
                                mediaUploadLabel(
                                  blockMediaUploadKey(block),
                                  block.mediaUrl
                                )
                              }}
                            </span>
                          </template>
                        </div>
                      </div>
                    </template>
                    <Input
                      v-else
                      v-model="block.mediaUrl"
                      :placeholder="
                        t(
                          'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_URL_PLACEHOLDER'
                        )
                      "
                    />
                  </div>

                  <div class="settings-input-group">
                    <label class="settings-input-label">
                      {{
                        block.type === 'video_note'
                          ? t(
                              'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.COMPANION_TEXT_LABEL'
                            )
                          : t(
                              'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.CAPTION_LABEL'
                            )
                      }}
                    </label>
                    <TextArea
                      v-if="block.type !== 'video_note'"
                      v-model="block.caption"
                      :placeholder="
                        t(
                          'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.CAPTION_PLACEHOLDER'
                        )
                      "
                      show-character-count
                    />
                    <TextArea
                      v-else
                      v-model="block.companionText"
                      :placeholder="
                        t(
                          'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.COMPANION_TEXT_PLACEHOLDER'
                        )
                      "
                      show-character-count
                    />
                  </div>

                  <template v-if="block.type === 'audio'">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        v-model="block.performer"
                        :placeholder="
                          t(
                            'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.AUDIO_PERFORMER_PLACEHOLDER'
                          )
                        "
                      />
                      <Input
                        v-model="block.title"
                        :placeholder="
                          t(
                            'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.AUDIO_TITLE_PLACEHOLDER'
                          )
                        "
                      />
                    </div>
                  </template>

                  <template v-if="block.type === 'video_note'">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        v-model="block.duration"
                        type="number"
                        min="0"
                        :placeholder="
                          t(
                            'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.VIDEO_NOTE_DURATION_PLACEHOLDER'
                          )
                        "
                      />
                      <Input
                        v-model="block.length"
                        type="number"
                        min="0"
                        :placeholder="
                          t(
                            'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.VIDEO_NOTE_LENGTH_PLACEHOLDER'
                          )
                        "
                      />
                    </div>
                  </template>
                </template>

                <template v-if="block.type === 'media_group'">
                  <div class="settings-input-group">
                    <label class="settings-input-label">
                      {{
                        t(
                          'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_GROUP.ITEMS_LABEL'
                        )
                      }}
                    </label>
                    <div
                      v-for="(item, itemIndex) in block.items"
                      :key="`media-group-item-${itemIndex}`"
                      class="block-item-row"
                    >
                      <select v-model="item.type" class="block-select">
                        <option value="photo">
                          {{
                            t(
                              'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_GROUP.TYPE_PHOTO'
                            )
                          }}
                        </option>
                        <option value="video">
                          {{
                            t(
                              'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_GROUP.TYPE_VIDEO'
                            )
                          }}
                        </option>
                        <option value="document">
                          {{
                            t(
                              'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_GROUP.TYPE_DOCUMENT'
                            )
                          }}
                        </option>
                        <option value="audio">
                          {{
                            t(
                              'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_GROUP.TYPE_AUDIO'
                            )
                          }}
                        </option>
                      </select>
                      <Input
                        v-if="!isUploadableMediaGroupItem(item)"
                        v-model="item.mediaUrl"
                        :placeholder="
                          t(
                            'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_URL_PLACEHOLDER'
                          )
                        "
                      />
                      <div
                        v-else
                        class="upload-zone upload-zone--compact block-item-upload"
                      >
                        <input
                          class="upload-zone__input"
                          type="file"
                          :accept="mediaGroupAccept(item)"
                          :disabled="
                            isMediaUploading(
                              mediaGroupItemUploadKey(block, itemIndex)
                            )
                          "
                          @change="
                            handleMediaGroupItemUpload(
                              $event,
                              block,
                              item,
                              itemIndex
                            )
                          "
                        />
                        <div
                          class="upload-zone__content upload-zone__content--compact"
                        >
                          <Spinner
                            v-if="
                              isMediaUploading(
                                mediaGroupItemUploadKey(block, itemIndex)
                              )
                            "
                          />
                          <template v-else>
                            <span
                              class="size-5 text-n-slate-10"
                              :class="
                                item.type === 'video'
                                  ? 'i-ph-video-camera-bold'
                                  : 'i-ph-image-bold'
                              "
                            />
                            <span class="text-xs text-n-slate-11 truncate">
                              {{
                                mediaUploadLabel(
                                  mediaGroupItemUploadKey(block, itemIndex),
                                  item.mediaUrl
                                )
                              }}
                            </span>
                          </template>
                        </div>
                      </div>
                      <Input
                        v-model="item.caption"
                        :placeholder="
                          t(
                            'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_GROUP.CAPTION_PLACEHOLDER'
                          )
                        "
                      />
                      <Button
                        icon="i-ph-trash-bold"
                        color="ruby"
                        variant="faded"
                        size="sm"
                        @click="removeMediaGroupItem(block, itemIndex)"
                      />
                    </div>
                    <button
                      v-if="block.items.length < 10"
                      type="button"
                      class="add-button-link"
                      @click="addMediaGroupItem(block)"
                    >
                      <span class="i-ph-plus-bold size-4" />
                      {{
                        t(
                          'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_GROUP.ADD_ITEM'
                        )
                      }}
                    </button>
                  </div>

                  <div class="settings-input-group">
                    <label class="settings-input-label">
                      {{
                        t(
                          'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_GROUP.FOLLOW_UP_TEXT_LABEL'
                        )
                      }}
                    </label>
                    <Input
                      v-model="block.followUpText"
                      :placeholder="
                        t(
                          'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_GROUP.FOLLOW_UP_TEXT_PLACEHOLDER'
                        )
                      "
                    />
                  </div>
                </template>

                <template v-if="block.type === 'carousel'">
                  <div class="settings-input-group">
                    <label class="settings-input-label">
                      {{
                        t(
                          'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.CAROUSEL.CARDS_LABEL'
                        )
                      }}
                    </label>
                    <div
                      v-for="(card, cardIndex) in block.cards"
                      :key="`carousel-card-${cardIndex}`"
                      class="campaign-card"
                    >
                      <div class="campaign-card__header">
                        <span>
                          {{
                            t(
                              'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.CAROUSEL.CARD_TITLE',
                              { index: cardIndex + 1 }
                            )
                          }}
                        </span>
                        <Button
                          icon="i-ph-trash-bold"
                          color="ruby"
                          variant="faded"
                          size="sm"
                          @click="removeCarouselCard(block, cardIndex)"
                        />
                      </div>
                      <select v-model="card.mediaType" class="block-select">
                        <option value="image">
                          {{
                            t(
                              'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.CAROUSEL.TYPE_IMAGE'
                            )
                          }}
                        </option>
                        <option value="video">
                          {{
                            t(
                              'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.CAROUSEL.TYPE_VIDEO'
                            )
                          }}
                        </option>
                      </select>
                      <div class="upload-zone upload-zone--compact">
                        <input
                          class="upload-zone__input"
                          type="file"
                          :accept="
                            card.mediaType === 'video' ? 'video/*' : 'image/*'
                          "
                          :disabled="
                            isMediaUploading(
                              carouselCardUploadKey(block, cardIndex)
                            )
                          "
                          @change="
                            handleCarouselCardUpload(
                              $event,
                              block,
                              card,
                              cardIndex
                            )
                          "
                        />
                        <div
                          class="upload-zone__content upload-zone__content--compact"
                        >
                          <Spinner
                            v-if="
                              isMediaUploading(
                                carouselCardUploadKey(block, cardIndex)
                              )
                            "
                          />
                          <template v-else>
                            <span
                              class="size-5 text-n-slate-10"
                              :class="
                                card.mediaType === 'video'
                                  ? 'i-ph-video-camera-bold'
                                  : 'i-ph-image-bold'
                              "
                            />
                            <span class="text-sm text-n-slate-11">
                              {{
                                mediaUploadLabel(
                                  carouselCardUploadKey(block, cardIndex),
                                  card.mediaUrl
                                )
                              }}
                            </span>
                          </template>
                        </div>
                      </div>
                      <TextArea
                        v-model="card.caption"
                        :placeholder="
                          t(
                            'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.CAROUSEL.CAPTION_PLACEHOLDER'
                          )
                        "
                        show-character-count
                      />
                      <div class="campaign-card__buttons">
                        <div
                          v-for="(button, buttonIndex) in card.buttons"
                          :key="`card-btn-${buttonIndex}`"
                          class="button-row"
                        >
                          <Input
                            v-model="button.text"
                            :placeholder="
                              t(
                                'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.BUTTON_TEXT_PLACEHOLDER'
                              )
                            "
                          />
                          <Input
                            v-model="button.url"
                            :placeholder="
                              t(
                                'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.BUTTON_URL_PLACEHOLDER'
                              )
                            "
                          />
                          <Button
                            icon="i-ph-trash-bold"
                            color="ruby"
                            variant="faded"
                            size="sm"
                            @click="removeCarouselCardButton(card, buttonIndex)"
                          />
                          <label class="button-row__webapp">
                            <input
                              v-model="button.openInAppPopup"
                              type="checkbox"
                              class="w-4 h-4 rounded border-n-slate-7 text-n-blue-9 focus:ring-n-blue-9"
                            />
                            <span>{{
                              t(
                                'CAMPAIGN.TELEGRAM.EDITOR.FORM.BUTTONS.OPEN_IN_APP_POPUP'
                              )
                            }}</span>
                          </label>
                        </div>
                        <button
                          v-if="card.buttons.length < 8"
                          type="button"
                          class="add-button-link"
                          @click="addCarouselCardButton(card)"
                        >
                          <span class="i-ph-plus-bold size-4" />
                          {{
                            t(
                              'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.CAROUSEL.ADD_CARD_BUTTON'
                            )
                          }}
                        </button>
                      </div>
                    </div>

                    <button
                      type="button"
                      class="add-button-link"
                      @click="addCarouselCard(block)"
                    >
                      <span class="i-ph-plus-bold size-4" />
                      {{
                        t(
                          'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.CAROUSEL.ADD_CARD'
                        )
                      }}
                    </button>
                  </div>
                </template>

                <template v-if="block.type === 'paid_media'">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                      v-model="block.starCount"
                      type="number"
                      min="1"
                      max="25000"
                      :placeholder="
                        t(
                          'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.PAID_MEDIA.STAR_COUNT_PLACEHOLDER'
                        )
                      "
                    />
                    <Input
                      v-model="block.payload"
                      :placeholder="
                        t(
                          'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.PAID_MEDIA.PAYLOAD_PLACEHOLDER'
                        )
                      "
                    />
                  </div>
                  <TextArea
                    v-model="block.caption"
                    :placeholder="
                      t(
                        'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.PAID_MEDIA.CAPTION_PLACEHOLDER'
                      )
                    "
                    show-character-count
                  />
                  <div class="settings-input-group">
                    <label class="settings-input-label">
                      {{
                        t(
                          'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.PAID_MEDIA.ITEMS_LABEL'
                        )
                      }}
                    </label>
                    <div
                      v-for="(item, itemIndex) in block.items"
                      :key="`paid-media-item-${itemIndex}`"
                      class="block-item-row"
                    >
                      <select v-model="item.type" class="block-select">
                        <option value="image">
                          {{
                            t(
                              'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.PAID_MEDIA.TYPE_IMAGE'
                            )
                          }}
                        </option>
                        <option value="video">
                          {{
                            t(
                              'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.PAID_MEDIA.TYPE_VIDEO'
                            )
                          }}
                        </option>
                      </select>
                      <div
                        class="upload-zone upload-zone--compact block-item-upload"
                      >
                        <input
                          class="upload-zone__input"
                          type="file"
                          :accept="
                            item.type === 'video' ? 'video/*' : 'image/*'
                          "
                          :disabled="
                            isMediaUploading(
                              paidMediaItemUploadKey(block, itemIndex)
                            )
                          "
                          @change="
                            handlePaidMediaItemUpload(
                              $event,
                              block,
                              item,
                              itemIndex
                            )
                          "
                        />
                        <div
                          class="upload-zone__content upload-zone__content--compact"
                        >
                          <Spinner
                            v-if="
                              isMediaUploading(
                                paidMediaItemUploadKey(block, itemIndex)
                              )
                            "
                          />
                          <template v-else>
                            <span
                              class="size-5 text-n-slate-10"
                              :class="
                                item.type === 'video'
                                  ? 'i-ph-video-camera-bold'
                                  : 'i-ph-image-bold'
                              "
                            />
                            <span class="text-xs text-n-slate-11 truncate">
                              {{
                                mediaUploadLabel(
                                  paidMediaItemUploadKey(block, itemIndex),
                                  item.mediaUrl
                                )
                              }}
                            </span>
                          </template>
                        </div>
                      </div>
                      <Button
                        icon="i-ph-trash-bold"
                        color="ruby"
                        variant="faded"
                        size="sm"
                        @click="removePaidMediaItem(block, itemIndex)"
                      />
                    </div>
                    <button
                      v-if="block.items.length < 10"
                      type="button"
                      class="add-button-link"
                      @click="addPaidMediaItem(block)"
                    >
                      <span class="i-ph-plus-bold size-4" />
                      {{
                        t(
                          'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.PAID_MEDIA.ADD_ITEM'
                        )
                      }}
                    </button>
                  </div>
                </template>

                <template
                  v-if="
                    block.type !== 'carousel' && block.type !== 'media_group'
                  "
                >
                  <div class="settings-input-group">
                    <label class="settings-input-label">
                      {{
                        t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.BUTTONS_LABEL')
                      }}
                    </label>
                    <div
                      v-for="(button, buttonIndex) in block.buttons"
                      :key="`block-button-${buttonIndex}`"
                      class="button-row"
                    >
                      <Input
                        v-model="button.text"
                        :placeholder="
                          t(
                            'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.BUTTON_TEXT_PLACEHOLDER'
                          )
                        "
                      />
                      <Input
                        v-model="button.url"
                        :placeholder="
                          t(
                            'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.BUTTON_URL_PLACEHOLDER'
                          )
                        "
                      />
                      <Button
                        icon="i-ph-trash-bold"
                        color="ruby"
                        variant="faded"
                        size="sm"
                        @click="removeBlockButton(block, buttonIndex)"
                      />
                      <label class="button-row__webapp">
                        <input
                          v-model="button.openInAppPopup"
                          type="checkbox"
                          class="w-4 h-4 rounded border-n-slate-7 text-n-blue-9 focus:ring-n-blue-9"
                        />
                        <span>{{
                          t(
                            'CAMPAIGN.TELEGRAM.EDITOR.FORM.BUTTONS.OPEN_IN_APP_POPUP'
                          )
                        }}</span>
                      </label>
                    </div>
                    <button
                      v-if="block.buttons.length < 8"
                      type="button"
                      class="add-button-link"
                      @click="addBlockButton(block)"
                    >
                      <span class="i-ph-plus-bold size-4" />
                      {{ t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.ADD_BUTTON') }}
                    </button>
                  </div>
                </template>

                <template v-if="block.type === 'media_group'">
                  <div class="settings-input-group">
                    <label class="settings-input-label">
                      {{
                        t(
                          'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_GROUP.FOLLOW_UP_BUTTONS_LABEL'
                        )
                      }}
                    </label>
                    <div
                      v-for="(button, buttonIndex) in block.buttons"
                      :key="`media-group-button-${buttonIndex}`"
                      class="button-row"
                    >
                      <Input
                        v-model="button.text"
                        :placeholder="
                          t(
                            'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.BUTTON_TEXT_PLACEHOLDER'
                          )
                        "
                      />
                      <Input
                        v-model="button.url"
                        :placeholder="
                          t(
                            'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.BUTTON_URL_PLACEHOLDER'
                          )
                        "
                      />
                      <Button
                        icon="i-ph-trash-bold"
                        color="ruby"
                        variant="faded"
                        size="sm"
                        @click="removeBlockButton(block, buttonIndex)"
                      />
                      <label class="button-row__webapp">
                        <input
                          v-model="button.openInAppPopup"
                          type="checkbox"
                          class="w-4 h-4 rounded border-n-slate-7 text-n-blue-9 focus:ring-n-blue-9"
                        />
                        <span>{{
                          t(
                            'CAMPAIGN.TELEGRAM.EDITOR.FORM.BUTTONS.OPEN_IN_APP_POPUP'
                          )
                        }}</span>
                      </label>
                    </div>
                    <button
                      v-if="block.buttons.length < 8"
                      type="button"
                      class="add-button-link"
                      @click="addBlockButton(block)"
                    >
                      <span class="i-ph-plus-bold size-4" />
                      {{
                        t(
                          'CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.MEDIA_GROUP.ADD_FOLLOW_UP_BUTTON'
                        )
                      }}
                    </button>
                  </div>
                </template>
              </div>
            </div>
          </section>
        </div>

        <!-- Right: Preview (sticky) -->
        <aside class="preview-sidebar">
          <div class="preview-sidebar__sticky">
            <div class="preview-sidebar__header">
              <h3
                class="text-sm font-semibold text-n-slate-12 uppercase tracking-wide"
              >
                {{ t('CAMPAIGN.TELEGRAM.EDITOR.PREVIEW.TITLE') }}
              </h3>
            </div>
            <div class="preview-sidebar__content">
              <TelegramCampaignPreview
                :bot-name="selectedBot?.bot_name || selectedBot?.name"
                :avatar-url="previewAvatarUrl"
                image-url=""
                message=""
                :buttons="[]"
                :blocks="previewBlocks"
              />
            </div>
          </div>
        </aside>
      </div>
    </div>

    <footer class="bottom-action-bar">
      <div class="bottom-action-bar__add">
        <ComboBox
          v-model="state.newBlockType"
          :options="BLOCK_TYPE_OPTIONS"
          dropdown-direction="up"
          :placeholder="t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.SELECT_TYPE')"
          class="campaign-combobox"
        />
        <Button
          icon="i-ph-plus-bold"
          color="slate"
          variant="outline"
          @click="addBlock"
        >
          {{ t('CAMPAIGN.TELEGRAM.EDITOR.FORM.BLOCKS.ADD_POST') }}
        </Button>
      </div>

      <div class="bottom-action-bar__schedule">
        <Input
          v-model="state.scheduledAt"
          type="datetime-local"
          :min="currentDateTime"
          :disabled="state.sendNow"
          :placeholder="
            t('CAMPAIGN.TELEGRAM.EDITOR.FORM.SCHEDULED_AT.PLACEHOLDER')
          "
          :message="state.sendNow ? '' : formErrors.scheduledAt"
          :message-type="formErrors.scheduledAt ? 'error' : 'info'"
        />
        <label class="bottom-action-bar__send-now">
          <input
            v-model="state.sendNow"
            type="checkbox"
            class="w-4 h-4 rounded border-n-slate-7 text-n-blue-9 focus:ring-n-blue-9"
          />
          <span>{{ t('CAMPAIGN.TELEGRAM.EDITOR.ACTIONS.SEND_NOW') }}</span>
        </label>
      </div>

      <button
        class="primary-action-btn"
        :disabled="isSubmitting"
        @click="submitCampaign"
      >
        <span
          v-if="isSubmitting"
          class="i-ph-spinner-bold size-4 animate-spin"
        />
        {{ submitLabel }}
      </button>
    </footer>
  </div>
</template>

<style scoped>
.settings-page,
.dark .settings-page {
  background: #ffffff !important;
  --wlab-bg-primary: #ffffff;
  --wlab-bg-card: #ffffff;
  --wlab-bg-input: #f9fafb;
  --wlab-bg-hover: #f3f4f6;
  --wlab-bg-secondary: #f9fafb;
  --wlab-border-light: #e5e7eb;
  --wlab-border-default: #e5e7eb;
  --wlab-text-primary: #111827;
  --wlab-text-secondary: #374151;
  --wlab-text-muted: #6b7280;
  --wlab-text-link: #0066ff;
  --wlab-text-link-hover: #ea6b00;
}

.settings-page :deep(.dark\:from-n-background),
.settings-page :deep(.from-n-background) {
  --tw-gradient-from: #ffffff var(--tw-gradient-from-position) !important;
}

.settings-page :deep(.text-n-slate-12) {
  color: #111827 !important;
}

.settings-page :deep(.text-n-slate-11) {
  color: #374151 !important;
}

.settings-page :deep(.text-n-slate-10) {
  color: #6b7280 !important;
}

/* Telegram create campaign: keep inputs readable (ComboBox + MultiSelect) */
.settings-page :deep(.group\/combobox button) {
  background: #ffffff !important;
  border-color: #e5e7eb !important;
  color: #111827 !important;
}

.settings-page :deep(.group\/combobox button:hover:enabled) {
  background: #f9fafb !important;
}

.settings-page :deep(.group\/combobox button.focused) {
  border-color: #0066ff !important;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.12) !important;
}

.settings-page :deep(.bg-n-alpha-black2) {
  background: #ffffff !important;
}

.settings-page :deep(.bg-n-alpha-black1) {
  background: #f3f4f6 !important;
}

.settings-page :deep(.campaign-combobox .bg-n-solid-1) {
  background: #ffffff !important;
}

.settings-page :deep(.campaign-combobox .border-n-strong) {
  border-color: #e5e7eb !important;
}

.settings-page :deep(.campaign-combobox .i-lucide-search) {
  color: #6b7280 !important;
}

.settings-page :deep(.campaign-combobox [role='option']:hover),
.settings-page :deep(.campaign-combobox [role='option'][aria-selected='true']) {
  background: #f3f4f6 !important;
}

.settings-page :deep(input::placeholder),
.settings-page :deep(textarea::placeholder) {
  color: #9ca3af !important;
}

.settings-page {
  background-color: #ffffff;
}

.settings-header {
  position: relative;
  padding: 12px 32px;
  background-color: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  overflow: hidden;
}

.header-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.header-glow {
  position: absolute;
  top: -50px;
  right: 60px;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(99, 102, 241, 0.06) 0%,
    transparent 70%
  );
}

.header-pattern {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(
    rgba(99, 102, 241, 0.04) 1px,
    transparent 1px
  );
  background-size: 20px 20px;
}

.header-inner {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.header-icon-box {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(
    135deg,
    rgba(99, 102, 241, 0.15) 0%,
    rgba(139, 92, 246, 0.08) 100%
  );
  border: 1px solid rgba(99, 102, 241, 0.2);
  color: #818cf8;
}

.header-text {
  min-width: 0;
}

.header-title {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #111827;
  margin: 0;
  line-height: 1.2;
}

.header-desc {
  font-size: 11px;
  color: #6b7280;
  margin: 2px 0 0;
}

.settings-content {
  background-color: #ffffff;
  min-height: 0;
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.back-btn:hover {
  background: #f3f4f6;
  color: #111827;
}

.primary-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 8px 16px;
  font-size: 14px;
  line-height: 16px;
  font-weight: 600;
  font-family:
    'Excon',
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif;
  background-color: #0066ff;
  color: #ffffff;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.primary-action-btn:hover {
  background-color: #ea6b00;
}

.primary-action-btn:active {
  transform: scale(0.98);
}

.primary-action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.settings-section {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  padding: 20px 32px;
  border-bottom: 1px solid #e5e7eb;
}

@media (min-width: 1024px) {
  .settings-section {
    grid-template-columns: minmax(280px, 1fr) minmax(0, 2fr);
    gap: 2rem;
  }
}

.settings-section__info {
  flex-shrink: 0;
}

.settings-section__content {
  flex: 1;
  min-width: 0;
}

.settings-input-group {
  display: flex;
  flex-direction: column;
}

.settings-input-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--wlab-text-secondary);
  margin-bottom: 0.5rem;
}

.upload-zone {
  position: relative;
  min-height: 3.5rem;
  border: 1px dashed var(--wlab-border-default);
  border-radius: 0.75rem;
  background: var(--wlab-bg-input);
  transition: all 0.2s ease;
  cursor: pointer;
}

.upload-zone:hover {
  background: var(--wlab-bg-hover);
  border-color: var(--wlab-text-muted);
}

.upload-zone__input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.upload-zone__input:disabled {
  cursor: not-allowed;
}

.upload-zone__content {
  min-height: 3.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  padding: 0.875rem 1rem;
}

.upload-zone--compact {
  min-height: 2.75rem;
}

.upload-zone__content--compact {
  min-height: 2.75rem;
  justify-content: flex-start;
  padding: 0.5rem 0.75rem;
}

.button-row {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 0.5rem;
  align-items: start;
}

.button-row__webapp {
  grid-column: 1 / -1;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: var(--wlab-text-secondary);
}

.block-add-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.5rem;
  align-items: center;
}

.block-add-select {
  min-width: 0;
}

.campaign-block {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.875rem;
  border: 1px solid var(--wlab-border-default);
  border-radius: 0.75rem;
  background: var(--wlab-bg-input);
}

.campaign-block__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.campaign-block__title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--wlab-text-primary);
}

.block-item-row {
  display: grid;
  grid-template-columns: 130px 1fr 1fr auto;
  gap: 0.5rem;
  align-items: start;
  margin-bottom: 0.5rem;
}

.block-item-upload {
  width: 100%;
}

.block-select {
  width: 100%;
  min-height: 2.5rem;
  padding: 0.5rem 0.625rem;
  border-radius: 0.625rem;
  border: 1px solid var(--wlab-border-default);
  background: var(--wlab-bg-secondary);
  color: var(--wlab-text-primary);
  font-size: 0.8125rem;
}

.campaign-card {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 0.625rem;
  border: 1px solid var(--wlab-border-default);
  background: var(--wlab-bg-secondary);
  margin-bottom: 0.625rem;
}

.campaign-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.75rem;
  color: var(--wlab-text-secondary);
}

.campaign-card__buttons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.add-button-link {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--wlab-text-link);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: color 0.15s ease;
}

.add-button-link:hover {
  color: var(--wlab-text-link-hover);
}

.preview-sidebar {
  background: var(--wlab-bg-secondary);
  border-left: 1px solid #e5e7eb;
}

.preview-sidebar__sticky {
  position: sticky;
  top: 0;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.preview-sidebar__header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.preview-sidebar__content {
  flex: 1;
  overflow-y: auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 1rem 1rem 1.5rem;
}

.preview-sidebar__content :deep(.telegram-preview-wrap) {
  padding-top: 0.5rem;
  transform: scale(0.88);
  transform-origin: top center;
}

.bottom-action-bar {
  position: relative;
  z-index: 40;
  overflow: visible;
  flex-shrink: 0;
  display: grid;
  grid-template-columns: minmax(220px, 360px) minmax(260px, 1fr) auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.75rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
}

.bottom-action-bar__add {
  position: relative;
  overflow: visible;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.5rem;
  align-items: center;
}

.bottom-action-bar__schedule {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: center;
}

.bottom-action-bar__send-now {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--wlab-text-secondary);
  white-space: nowrap;
}

@media (max-width: 980px) {
  .bottom-action-bar {
    grid-template-columns: 1fr;
  }

  .bottom-action-bar__schedule {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1280px) {
  .preview-sidebar {
    display: none;
  }
}
</style>
